import { mapPermissions } from '@/helpers/PermissionHelper'
import { sortRoles } from '@/helpers/RolesHelper'
import type { IdToken } from '@/models/Authentication/IdToken'
import type { UserInfo } from '@/models/UserInfo'
import { useUserStore } from '@/stores/UserStore'
import { InMemoryWebStorage, User, UserManager, WebStorageStateStore, type UserManagerSettings } from 'oidc-client-ts'
import { ref } from 'vue'

export class AuthService {
  private userManager: UserManager

  public isAuthenticated = ref(false)
  public user = ref<User | null>(null)
  public decodedToken = ref<IdToken | null>(null)

  constructor() {
    const settings: UserManagerSettings = {
      authority: import.meta.env.VITE_IDM_AUTHORITY,
      client_id: import.meta.env.VITE_IDM_CLIENTID,
      client_secret: import.meta.env.VITE_IDM_CLIENTSECRET,
      redirect_uri: import.meta.env.VITE_IDM_REDIRECT_URI,
      response_type: 'code',
      scope: import.meta.env.VITE_IDM_SCOPE,

      metadata: {
        issuer: import.meta.env.VITE_IDM_ISSUER,
        authorization_endpoint: import.meta.env.VITE_IDM_AUTH_ENDPOINT,
        token_endpoint: import.meta.env.VITE_IDM_TOKEN_ENDPOINT,
        end_session_endpoint: import.meta.env.VITE_IDM_ENDSESSION_ENDPOINT
      },
      userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() })
    }

    this.userManager = new UserManager(settings)

    this.userManager.events.addUserLoaded(loadedUser => {
      this.user.value = loadedUser
      this.isAuthenticated.value = true
    })

    this.userManager.events.addUserUnloaded(() => {
      this.user.value = null
      this.isAuthenticated.value = false
    })
  }

  decodeToken(): IdToken | null {
    if (this.user.value && this.user.value.id_token) {
      const tokenParts = this.user.value.id_token.split('.')
      if (!tokenParts[1]) {
        return null
      }
      const payload = JSON.parse(atob(tokenParts[1]))

      const permissions = mapPermissions(payload.permission)
      const roles: string[] = Array.isArray(payload.role) ? sortRoles(payload.role) : [payload.role]
      const userStore = useUserStore()
      const displayName: string = (payload.given_name ? `${payload.given_name} ` : '') + (payload.family_name ?? '')
      userStore.updateUser(<UserInfo>{
        displayName: displayName,
        mail: payload.email,
        servicePermissions: permissions,
        roles: roles
      })

      return {
        nbf: payload.nbf,
        exp: payload.exp,
        at_hash: payload.at_hash,
        aud: payload.aud,
        iat: payload.iat,
        iss: payload.iss,
        email: payload.email,
        role: payload.role,
        sub: payload.sub,
        permissions: permissions
      }
    }
    return null
  }

  async login() {
    try {
      await this.userManager.signinRedirect()
    } catch (error) {
      console.error('Error while trying to login:', error)
    }
  }

  async handleCallback() {
    try {
      const user = await this.userManager.signinRedirectCallback()
      this.decodedToken.value = this.decodeToken()
      return user
    } catch (error) {
      console.error('Callback error:', error)
      throw error
    }
  }

  async logout() {
    try {
      await this.userManager.removeUser()
      window.location.href = import.meta.env.VITE_IDM_ENDSESSION_ENDPOINT
    } catch (error) {
      console.error('Error while disconnecting:', error)
    }
  }

  async renewToken() {
    try {
      const renewedUser = await this.userManager.signinSilent()
      this.user.value = renewedUser
      this.decodedToken.value = this.decodeToken()
      return renewedUser
    } catch (error) {
      console.error('Failed to renew token:', error)
      await this.userManager.removeUser()
      await this.login()
    }
  }

  async checkAuthentication() {
    try {
      if (import.meta.env.MODE === 'ci') {
        return true
      }
      if (this.decodedToken.value) {
        const currentTimestamp = Math.floor(Date.now() / 1000) // timestamp en secondes
        const timeRemaining = this.decodedToken.value?.exp - currentTimestamp
        if (timeRemaining <= 60 && timeRemaining > 0) {
          await this.renewToken()
        }
        if (timeRemaining < 0) {
          this.user.value = null
          return null
        }
        return true
      }

      this.user.value = null
      return null
    } catch (error) {
      console.error('Cannot find user:', error)
      return null
    }
  }

  async getAccessToken(): Promise<string | null> {
    try {
      const user = await this.userManager.getUser()
      return user ? user.access_token : null
    } catch (error) {
      console.error('Token retrieving error:', error)
      return null
    }
  }
}

export const authService = new AuthService()
